import { API_BASE_URL } from './api';
import { authService } from './authService';

const authHeaders = (): Record<string, string> => ({ ...authService.getAuthHeaders() } as Record<string, string>);
const authJsonHeaders = (): Record<string, string> => ({ 'Content-Type': 'application/json', ...authHeaders() });

export const EventRsvpStatus = {
  Going: 'Going',
  NotGoing: 'NotGoing',
} as const;
export type EventRsvpStatusValue = typeof EventRsvpStatus[keyof typeof EventRsvpStatus];

export interface DiveEvent {
  eventId: string;
  hostUserId: string;
  hostDisplayName: string;
  title: string;
  description?: string | null;
  photoKey?: string | null;
  startTime: string;
  endTime?: string | null;
  location?: string | null;
  groupId?: string | null;
  clubId?: string | null;
  /** Set for region-community events, which are visible to every signed-in diver. */
  regionSlug?: string | null;
  goingCount: number;
  /** Host opt-in to show this event to signed-out visitors on the public home page. */
  isPublic?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EventRsvp {
  eventId: string;
  userId: string;
  displayName: string;
  profilePhotoUrl?: string | null;
  status: EventRsvpStatusValue;
  respondedAt: string;
}

export interface CreateEventRequest {
  title: string;
  description?: string;
  photoKey?: string;
  startTime: string;
  endTime?: string;
  location?: string;
  groupId?: string;
  clubId?: string;
  /** Host an event for a region community (the host must have joined it). */
  regionSlug?: string;
  isPublic?: boolean;
}

export interface UpdateEventRequest {
  title?: string;
  description?: string;
  photoKey?: string;
  startTime?: string;
  endTime?: string;
  location?: string;
}

// Same public bucket every other photo upload in this app uses — no dedicated
// events-photo prefix or infra needed.
export const eventPhotoUrl = (photoKey: string): string =>
  `https://aidivecoach-post-photos.s3.us-east-2.amazonaws.com/${photoKey}`;

const errorFrom = async (res: Response, fallback: string): Promise<string> => {
  try {
    const body = await res.text();
    return body || fallback;
  } catch {
    return fallback;
  }
};

export const eventService = {
  createEvent: async (request: CreateEventRequest): Promise<DiveEvent> => {
    const res = await fetch(`${API_BASE_URL}/events`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to create event'));
    return res.json();
  },

  updateEvent: async (eventId: string, request: UpdateEventRequest): Promise<DiveEvent> => {
    const res = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(eventId)}`, {
      method: 'PATCH',
      headers: authJsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to update event'));
    return res.json();
  },

  deleteEvent: async (eventId: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(eventId)}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to delete event'));
  },

  getEvent: async (eventId: string): Promise<DiveEvent> => {
    const res = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(eventId)}`, { headers: authHeaders() });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to load event'));
    return res.json();
  },

  getEventsForGroup: async (groupId: string): Promise<DiveEvent[]> => {
    const res = await fetch(`${API_BASE_URL}/events/group/${encodeURIComponent(groupId)}`, { headers: authHeaders() });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to load events'));
    return res.json();
  },

  getEventsForClub: async (clubId: string): Promise<DiveEvent[]> => {
    const res = await fetch(`${API_BASE_URL}/events/club/${encodeURIComponent(clubId)}`, { headers: authHeaders() });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to load events'));
    return res.json();
  },

  /** Upcoming events across every region community the caller has joined, soonest first. */
  getEventsForMyCommunities: async (): Promise<DiveEvent[]> => {
    const res = await fetch(`${API_BASE_URL}/events/communities`, { headers: authHeaders() });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to load events'));
    return res.json();
  },

  getEventsForRegion: async (regionSlug: string): Promise<DiveEvent[]> => {
    const res = await fetch(`${API_BASE_URL}/events/region/${encodeURIComponent(regionSlug)}`, { headers: authHeaders() });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to load events'));
    return res.json();
  },

  getRsvps: async (eventId: string): Promise<EventRsvp[]> => {
    const res = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(eventId)}/rsvps`, { headers: authHeaders() });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to load RSVPs'));
    return res.json();
  },

  rsvp: async (eventId: string, status: EventRsvpStatusValue = EventRsvpStatus.Going): Promise<EventRsvp> => {
    const res = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(eventId)}/rsvp`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify({ status }),
    });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to RSVP'));
    return res.json();
  },
};
